import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Save, KeyRound, User, Mail, Phone, Briefcase, Building2, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../api/api';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

function Avatar({ user, size = 80 }) {
  const initials = (user?.full_name || user?.username || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  if (user?.avatar_url) {
    return (
      <img
        src={`${API_BASE}${user.avatar_url}`}
        alt={user.full_name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--accent-light)' }}
        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--accent)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.3, fontWeight: 700, flexShrink: 0,
      border: '3px solid var(--accent-light)',
    }}>
      {initials}
    </div>
  );
}

export { Avatar };

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const fileRef = useRef();
  const [tab, setTab] = useState('profile');

  // Profile form
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    designation: user?.designation || '',
    department: user?.department || '',
  });

  // Password form
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdError, setPwdError] = useState('');

  const setP = (k, v) => setProfileForm(f => ({ ...f, [k]: v }));
  const setPwd = (k, v) => { setPwdForm(f => ({ ...f, [k]: v })); setPwdError(''); };

  const profileMutation = useMutation({
    mutationFn: () => authApi.updateProfile(profileForm),
    onSuccess: async () => { await refreshUser(); toast.success('Profile updated successfully'); },
    onError: (e) => toast.error(e.error || 'Update failed'),
  });

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword({ current_password: pwdForm.current_password, new_password: pwdForm.new_password }),
    onSuccess: () => { toast.success('Password changed successfully'); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); },
    onError: (e) => toast.error(e.error || 'Password change failed'),
  });

  const avatarMutation = useMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('avatar', file);
      return authApi.uploadAvatar(form);
    },
    onSuccess: async () => { await refreshUser(); toast.success('Profile photo updated'); },
    onError: (e) => toast.error(e.error || 'Upload failed'),
  });

  const handleProfileSubmit = (e) => { e.preventDefault(); profileMutation.mutate(); };
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm_password) { setPwdError('New passwords do not match'); return; }
    if (pwdForm.new_password.length < 6) { setPwdError('Password must be at least 6 characters'); return; }
    passwordMutation.mutate();
  };
  const handleFileChange = (e) => { const f = e.target.files[0]; if (f) avatarMutation.mutate(f); };

  const roleBadge = { admin: '#accent', manager: 'blue', operator: 'green' };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">My Profile</span>
      </div>
      <div className="page-content" style={{ maxWidth: 760 }}>

        {/* Profile Header Card */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ padding: '28px 28px 24px', display: 'flex', alignItems: 'center', gap: 24 }}>
            {/* Avatar with upload button */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar user={user} size={88} />
              <button
                onClick={() => fileRef.current.click()}
                disabled={avatarMutation.isPending}
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'var(--accent)', border: '2px solid #fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff',
                }}
                title="Change photo"
              >
                <Camera size={13} />
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-800)', marginBottom: 4 }}>
                {user?.full_name || user?.username}
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 10 }}>
                {user?.designation || 'No designation set'}{user?.department ? ` · ${user.department}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className={`badge badge-${user?.role === 'admin' ? 'approved' : user?.role === 'manager' ? 'issued' : 'active'}`} style={{ textTransform: 'capitalize' }}>
                  <ShieldCheck size={10} style={{ marginRight: 4 }} />{user?.role}
                </span>
                <span className="badge badge-active">{user?.status}</span>
              </div>
            </div>

            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--gray-400)' }}>
              <div>@{user?.username}</div>
              <div style={{ marginTop: 4 }}>{user?.email}</div>
              {user?.last_login && (
                <div style={{ marginTop: 4 }}>
                  Last login: {new Date(user.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>

          {/* Upload hint */}
          {avatarMutation.isPending && (
            <div style={{ padding: '8px 28px', background: 'var(--accent-light)', fontSize: 12, color: 'var(--accent-text)' }}>
              Uploading photo…
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
          {[
            { id: 'profile', label: 'Profile Details', icon: User },
            { id: 'password', label: 'Change Password', icon: KeyRound },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '10px 18px', border: 'none', background: 'transparent',
                fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                color: tab === id ? 'var(--accent)' : 'var(--gray-400)',
                borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.12s',
              }}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Profile Details Tab */}
        {tab === 'profile' && (
          <div className="card">
            <div className="card-header">
              <User size={15} />
              <span className="card-title">Personal Information</span>
            </div>
            <form onSubmit={handleProfileSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group form-full">
                    <label className="form-label"><User size={11} style={{ marginRight: 5 }} />Full Name</label>
                    <input className="form-control" value={profileForm.full_name} onChange={e => setP('full_name', e.target.value)} placeholder="Your full name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Mail size={11} style={{ marginRight: 5 }} />Email Address</label>
                    <input className="form-control" type="email" value={profileForm.email} onChange={e => setP('email', e.target.value)} placeholder="your@email.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Phone size={11} style={{ marginRight: 5 }} />Phone Number</label>
                    <input className="form-control" value={profileForm.phone} onChange={e => setP('phone', e.target.value)} placeholder="Mobile number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Briefcase size={11} style={{ marginRight: 5 }} />Designation</label>
                    <input className="form-control" value={profileForm.designation} onChange={e => setP('designation', e.target.value)} placeholder="e.g. Store Manager" />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Building2 size={11} style={{ marginRight: 5 }} />Department</label>
                    <input className="form-control" value={profileForm.department} onChange={e => setP('department', e.target.value)} placeholder="e.g. Operations" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" disabled={profileMutation.isPending}>
                  <Save size={14} />
                  {profileMutation.isPending ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Change Password Tab */}
        {tab === 'password' && (
          <div className="card">
            <div className="card-header">
              <KeyRound size={15} />
              <span className="card-title">Change Password</span>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body">
                {pwdError && <div className="alert alert-error">{pwdError}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <input className="form-control" type="password" value={pwdForm.current_password} onChange={e => setPwd('current_password', e.target.value)} required placeholder="Your current password" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input className="form-control" type="password" value={pwdForm.new_password} onChange={e => setPwd('new_password', e.target.value)} required placeholder="At least 6 characters" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      className="form-control" type="password"
                      value={pwdForm.confirm_password} onChange={e => setPwd('confirm_password', e.target.value)} required placeholder="Repeat new password"
                      style={{ borderColor: pwdForm.confirm_password && pwdForm.new_password !== pwdForm.confirm_password ? 'var(--red)' : undefined }}
                    />
                    {pwdForm.confirm_password && pwdForm.new_password !== pwdForm.confirm_password && (
                      <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>Passwords do not match</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" disabled={passwordMutation.isPending}>
                  <KeyRound size={14} />
                  {passwordMutation.isPending ? 'Changing…' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
}